import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'nirmaan_store_cart_v1';

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const writeStorage = (items) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(readStorage);

  useEffect(() => { writeStorage(items); }, [items]);

  const addToCart = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find(i => i.product_id === product.product_id);
      if (existing) {
        return prev.map(i => i.product_id === product.product_id ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, {
        product_id: product.product_id,
        product_name: product.product_name,
        product_brand: product.product_brand,
        product_size: product.product_size,
        product_color: product.product_color,
        unit: product.unit,
        selling_price: product.selling_price,
        mrp: product.mrp || 0,
        image_url: (product.image_urls && product.image_urls[0]) || null,
        quantity: qty,
      }];
    });
  }, []);

  const updateQty = useCallback((product_id, qty) => {
    setItems((prev) => qty <= 0
      ? prev.filter(i => i.product_id !== product_id)
      : prev.map(i => i.product_id === product_id ? { ...i, quantity: qty } : i)
    );
  }, []);

  const removeFromCart = useCallback((product_id) => {
    setItems((prev) => prev.filter(i => i.product_id !== product_id));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalCount = items.reduce((s, i) => s + i.quantity, 0);
  const totalValue = items.reduce((s, i) => s + i.quantity * (i.selling_price || 0), 0);

  return (
    <CartContext.Provider value={{ items, addToCart, updateQty, removeFromCart, clearCart, totalCount, totalValue }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};
